import { useForm, useSelector } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker, useSearchParams } from 'react-router'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { CodingGuidelinesTab } from '../../components/coding-guidelines/coding-guidelines-tab.tsx'
import { type UploadJob } from '../../components/coding-guidelines/coding-guidelines-types.ts'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary.tsx'
import { FreeformTab } from '../../components/freeform/freeform-tab.tsx'
import { MarkdownEditor } from '../../components/markdown/markdown-editor.tsx'
import { PermissionNotice } from '../../components/permission-notice.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  useAdminRole,
  usePutOrganizationPreferences,
  useSuspenseGetOrganizationPreferences,
} from '../../utils/api-client/api-hooks.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { isLabsEnabled } from '../../utils/runtime-env.ts'
import { AbandonChangesDialog } from './abandon-changes-dialog.tsx'
import { IntegrationsTab, useConfiguredIntegrationCount } from './integrations-tab/integrations-tab.tsx'
import { NewerChangesDialog } from './newer-changes-dialog.tsx'
import * as styles from './organization-page.css.ts'

type AbandonChangesContext = 'cancel-edit' | 'navigate-away'

const ActiveTabSchema = z.enum(['preferences', 'coding-guidelines', 'freeform', 'integrations'])
type ActiveTab = z.infer<typeof ActiveTabSchema>

const parseActiveTab = ({ raw, tabs }: { raw: string | null; tabs: ActiveTab[] }): ActiveTab => {
  const parsed = ActiveTabSchema.catch('preferences').parse(raw)
  return tabs.includes(parsed) ? parsed : 'preferences'
}

const setTabParam = (current: URLSearchParams, tab: ActiveTab): URLSearchParams => {
  const next = new URLSearchParams(current)
  if (tab === 'preferences') {
    next.delete('tab')
  } else {
    next.set('tab', tab)
  }
  return next
}

const ALL_TABS: { value: ActiveTab; label: string }[] = [
  { value: 'preferences', label: 'Preferences' },
  { value: 'coding-guidelines', label: 'Secure Coding Guidelines' },
  { value: 'freeform', label: 'Findings intake' },
  { value: 'integrations', label: 'Integrations' },
]

type OrganizationPageProps = {
  initialUploadJobs?: UploadJob[]
}

export const OrganizationPage: React.FC<OrganizationPageProps> = ({ initialUploadJobs }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isFreeformEnabled = isLabsEnabled()
  const tabs = useMemo(
    () => ALL_TABS.filter(({ value }) => value !== 'freeform' || isFreeformEnabled),
    [isFreeformEnabled]
  )
  const activeTab = useMemo(
    () => parseActiveTab({ raw: searchParams.get('tab'), tabs: tabs.map(({ value }) => value) }),
    [searchParams, tabs]
  )

  const configuredIntegrationCount = useConfiguredIntegrationCount()

  const tabRefs = useRef(new Map<ActiveTab, HTMLButtonElement>())

  const selectTab = (tab: ActiveTab) => setSearchParams(prev => setTabParam(prev, tab), { replace: true })

  // Roving-tabindex arrow-key navigation for the WAI-ARIA tablist (automatic activation:
  // moving focus also selects, mirroring the click behavior).
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = tabs.findIndex(t => t.value === activeTab)
    const lastIndex = tabs.length - 1
    const nextIndex = match(event.key)
      .with('ArrowRight', () => (currentIndex === lastIndex ? 0 : currentIndex + 1))
      .with('ArrowLeft', () => (currentIndex === 0 ? lastIndex : currentIndex - 1))
      .with('Home', () => 0)
      .with('End', () => lastIndex)
      .otherwise(() => currentIndex)
    if (nextIndex === currentIndex) {
      return
    }
    const nextTab = tabs[nextIndex]
    if (!nextTab) {
      return
    }
    event.preventDefault()
    selectTab(nextTab.value)
    tabRefs.current.get(nextTab.value)?.focus()
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Organization</h1>
        <p className={styles.pageSubtitle}>Global settings and configuration</p>
      </div>

      <div className={styles.tabRow} role="tablist" aria-label="Organization settings">
        {tabs.map(({ value, label }) => {
          const isSelected = activeTab === value
          return (
            <button
              key={value}
              ref={node => {
                if (node) {
                  tabRefs.current.set(value, node)
                } else {
                  tabRefs.current.delete(value)
                }
              }}
              type="button"
              role="tab"
              id={`tab-${value}`}
              aria-controls={`tabpanel-${value}`}
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              className={styles.tab}
              onClick={() => selectTab(value)}
              onKeyDown={handleTabKeyDown}
            >
              {label}
              {value === 'integrations' && (
                <span className={styles.tabBadge} data-testid="integrations-count-badge">
                  {configuredIntegrationCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Every panel stays mounted (hidden) so in-progress work survives a tab switch: in-flight
          upload jobs and their SSE listeners, unsaved preference edits, the Findings intake tab's staged
          files and active upload session, and an open Setup or Manage drawer with credentials
          half-entered. */}
      <div
        id="tabpanel-coding-guidelines"
        role="tabpanel"
        aria-labelledby="tab-coding-guidelines"
        hidden={activeTab !== 'coding-guidelines'}
        className={styles.codingGuidelinesPanel}
      >
        <CodingGuidelinesTab initialUploadJobs={initialUploadJobs} />
      </div>

      <div
        id="tabpanel-preferences"
        role="tabpanel"
        aria-labelledby="tab-preferences"
        hidden={activeTab !== 'preferences'}
      >
        <ErrorBoundary fallbackTitle="Failed to load preferences">
          <Suspense fallback={<div className={styles.loading}>Loading...</div>}>
            <PreferencesTab />
          </Suspense>
        </ErrorBoundary>
      </div>

      {isFreeformEnabled && (
        <div id="tabpanel-freeform" role="tabpanel" aria-labelledby="tab-freeform" hidden={activeTab !== 'freeform'}>
          <FreeformTab />
        </div>
      )}

      <div
        id="tabpanel-integrations"
        role="tabpanel"
        aria-labelledby="tab-integrations"
        hidden={activeTab !== 'integrations'}
      >
        <IntegrationsTab />
      </div>
    </div>
  )
}

const PreferencesTab: React.FC = () => {
  const { addToast } = useToastService()
  const queryClient = useQueryClient()
  const { isAdmin, isRoleUnavailable } = useAdminRole()
  const { data: preferencesData } = useSuspenseGetOrganizationPreferences()
  const etag = preferencesData.etag
  const serverContent = preferencesData.data?.content ?? ''
  const form = useForm({ defaultValues: { contentMarkdown: serverContent } })
  const isDirty = useSelector(form.store, state => {
    const fieldMeta = state.fieldMeta['contentMarkdown']
    return fieldMeta ? !fieldMeta.isDefaultValue : false
  })
  const currentContent = useSelector(form.store, state => state.values.contentMarkdown)
  const [isEditing, setIsEditing] = useState(false)
  const [editorFocusToken, setEditorFocusToken] = useState(0)
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false)
  const [abandonContext, setAbandonContext] = useState<AbandonChangesContext>('cancel-edit')
  const [newerChangesDialogOpen, setNewerChangesDialogOpen] = useState(false)
  const blockedSaveContentRef = useRef<string>('')

  const abandonChanges = () => {
    form.reset()
    setIsEditing(false)
  }

  const showMutationError = useShowMutationError({ prefix: 'Failed to save preferences' })
  const saveMutation = usePutOrganizationPreferences({
    onSuccess: () => {
      addToast({
        message: <>Preferences successfully saved</>,
        variant: 'success',
      })
      const saved = form.getFieldValue('contentMarkdown')
      form.reset({ contentMarkdown: saved })
      setIsEditing(false)
    },
    onError: (error, ...rest) => {
      match(error.status)
        .with(412, () => {
          blockedSaveContentRef.current = form.getFieldValue('contentMarkdown')
          setNewerChangesDialogOpen(true)
        })
        .with(428, () => {
          addToast({
            message: <>Precondition required. Please refresh the page and try again.</>,
            variant: 'error',
            transience: 'sticky',
          })
        })
        // Reachable even with the controls hidden: the role can be revoked, or enforcement
        // turned on, while this page is open. Abandon the edit so the UI stops offering a save
        // that the API will keep refusing — dropping edit mode without resetting the form would
        // leave the read-only editor showing content the server never accepted, and would keep
        // the navigation blocker armed with no Cancel button left to clear it. The stale
        // `['current-user']` cache behind the Edit button is dropped in the mutation hook.
        .with(403, () => {
          addToast({
            message: <>You do not have permission to edit organization preferences.</>,
            variant: 'error',
            transience: 'sticky',
          })
          abandonChanges()
        })
        .otherwise(() => {
          showMutationError(error, ...rest)
        })
    },
  })

  const handleSave = () => {
    const isNeverCreated = preferencesData.data === null
    if (!etag && !isNeverCreated) {
      addToast({
        message: <>Unable to save: ETag not available. Please refresh the page.</>,
        variant: 'error',
      })
      return
    }
    saveMutation.mutate({ contentMarkdown: form.getFieldValue('contentMarkdown'), etag })
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditorFocusToken(current => current + 1)
  }

  const handleCancel = () => {
    if (isDirty) {
      setAbandonContext('cancel-edit')
      setAbandonDialogOpen(true)
      return
    }

    abandonChanges()
  }

  // A demotion can also arrive from the query rather than a refused save: `useCurrentUser` runs
  // under the global `staleTime: 0`, so tabbing away and back refetches it. Without this, losing
  // the role mid-edit unmounts both buttons while the editor stays writable — unsaved changes,
  // no Save, no Cancel, and the navigation blocker still armed. Mirror what the 403 path does.
  useEffect(() => {
    if (isAdmin === false && isEditing) {
      abandonChanges()
    }
  }, [isAdmin, isEditing])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setAbandonContext('navigate-away')
      setAbandonDialogOpen(true)
    }
  }, [blocker.state])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
      }
    }
    globalThis.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty])

  const isEmpty = !preferencesData.data && currentContent === ''
  const lastUpdatedAt = preferencesData.data?.updated_at
    ? formatDate({ date: preferencesData.data.updated_at, type: 'mm/dd/yyyy-hh:mm-ss' })
    : null
  const lastUpdatedBy = preferencesData.data?.updated_by ?? null
  const showLastUpdated = Boolean(lastUpdatedAt && lastUpdatedBy)

  const isNeverCreated = preferencesData.data === null
  const saveButtonState: NonNullable<React.ComponentProps<typeof DefaultButton>['state']> = saveMutation.isPending
    ? 'loading'
    : !isDirty || (!etag && !isNeverCreated)
      ? 'disabled'
      : 'default'

  const showEmptyStateCard = isEmpty && !isEditing
  const editorPlaceholder = ''

  return (
    <div className={styles.content}>
      <AbandonChangesDialog
        open={abandonDialogOpen}
        onCancel={() => {
          setAbandonDialogOpen(false)
          if (abandonContext === 'navigate-away' && blocker.state === 'blocked') {
            blocker.reset()
          }
        }}
        onAbandon={() => {
          setAbandonDialogOpen(false)
          abandonChanges()
          if (abandonContext === 'navigate-away' && blocker.state === 'blocked') {
            blocker.proceed()
          }
        }}
      />
      <NewerChangesDialog
        open={newerChangesDialogOpen}
        onCancel={() => {
          setNewerChangesDialogOpen(false)
        }}
        onCopyChanges={async () => {
          const textToCopy = blockedSaveContentRef.current || form.getFieldValue('contentMarkdown')
          await navigator.clipboard.writeText(textToCopy)
          setNewerChangesDialogOpen(false)
          setIsEditing(false)
          form.reset()
          queryClient.invalidateQueries({ queryKey: ['organization-preferences'] })
        }}
      />
      <div className={styles.introRow}>
        <p className={styles.introText}>
          Your organization’s custom guidelines and context, stored in Pixee’s memory across all repositories.
        </p>

        <div className={styles.metaRight}>
          {showLastUpdated && (
            <div className={styles.lastUpdatedBlock} data-testid="last-updated">
              <p className={styles.lastUpdatedLabel}>Last updated:</p>
              <p className={styles.lastUpdatedTimestamp}>
                {lastUpdatedAt} by @{lastUpdatedBy}
              </p>
            </div>
          )}

          {/* Only an admin may write, so only an admin is offered the controls. `undefined` is the
              answer not having arrived yet, and renders nothing rather than the message below —
              the editor is already read-only until Edit is pressed, so hiding costs nothing while
              guessing wrong would tell an admin they lack permission. */}
          {isAdmin === true &&
            (isEditing ? (
              <>
                <DefaultButton
                  buttonType="secondary"
                  size="small"
                  state={saveMutation.isPending ? 'disabled' : 'default'}
                  onClick={handleCancel}
                >
                  Cancel
                </DefaultButton>

                <DefaultButton
                  size="small"
                  state={saveButtonState}
                  onClick={handleSave}
                  loadingLabel="Saving preferences"
                >
                  Save changes
                </DefaultButton>
              </>
            ) : (
              <DefaultButton buttonType="secondary" size="small" state="default" onClick={handleEdit}>
                Edit
              </DefaultButton>
            ))}

          <PermissionNotice isAdmin={isAdmin} isRoleUnavailable={isRoleUnavailable}>
            Only administrators can edit preferences.
          </PermissionNotice>
        </div>
      </div>

      {showEmptyStateCard ? (
        <div className={styles.emptyStateCard}>
          <p className={styles.emptyStateTitle}>No preferences defined</p>
          <p className={styles.emptyStateSubtitle}>
            Open the Editor to define natural-language, rule-based preferences that help guide Pixee’s behavior.
          </p>
        </div>
      ) : (
        <MarkdownEditor
          value={currentContent}
          onChange={value => form.setFieldValue('contentMarkdown', value)}
          placeholder={editorPlaceholder}
          readOnly={!isEditing}
          focusToken={editorFocusToken}
        />
      )}
    </div>
  )
}
